import React, { useEffect, useState } from "react";
import { Card, Row, Col, Spinner, Button } from "react-bootstrap";
import { collection, doc, getDoc, query, getDocs, where } from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { Doughnut, Line } from "react-chartjs-2";
import { Link } from "react-router-dom";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [totalRequests, setTotalRequests] = useState(0);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [approvedRequests, setApprovedRequests] = useState(0);
  const [weeklyHours, setWeeklyHours] = useState(0);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);

      try {
        onAuthStateChanged(auth, async (user) => {
          if (user) {
            setCurrentUser(user);

            // Fetch current user information
            const userQuery = query(
              collection(db, "Users"),
              where("email", "==", user.email)
            );
            const userSnapshot = await getDocs(userQuery);
            const userInfo = userSnapshot.docs[0]?.data() || null;
            setUserData(userInfo);

            // Fetch permissions based on role
            if (userInfo?.role === "admin") {
              // Admin gets all permissions
              setPermissions({ all: true });
            } else if (userInfo?.role) {
              // Fetch permissions for the user's role
              const roleDoc = await getDoc(doc(db, "Roles", userInfo.role));
              if (roleDoc.exists()) {
                setPermissions(roleDoc.data()?.permissions || {});
              } else {
                console.error(`Role document for role ${userInfo.role} not found.`);
                setPermissions({});
              }
            }

            // Fetch leave requests data
            const leaveRequestsQuery = query(
              collection(db, "leave_requests"),
              where("email", "==", user.email)
            );
            const leaveRequestsSnapshot = await getDocs(leaveRequestsQuery);
            const leaveRequests = leaveRequestsSnapshot.docs.map((doc) => doc.data());

            setTotalRequests(leaveRequests.length);
            setPendingRequests(
              leaveRequests.filter((req) => req.status === "Pending").length
            );
            setApprovedRequests(
              leaveRequests.filter((req) => req.status === "Approved").length
            );

            // Fetch clock entries to calculate hours worked
            const clockEntriesQuery = query(
              collection(db, "clock_entries"),
              where("email", "==", user.email)
            );
            const clockEntriesSnapshot = await getDocs(clockEntriesQuery);
            const clockEntries = clockEntriesSnapshot.docs.map((doc) => doc.data());

            const now = new Date();
            const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
            let totalMinutes = 0;

            clockEntries.forEach((entry) => {
              const clockIn = new Date(entry.clockIn);
              const clockOut = entry.clockOut ? new Date(entry.clockOut) : null;

              if (clockIn >= startOfWeek && clockOut) {
                totalMinutes += (clockOut - clockIn) / 1000 / 60; // Convert ms to minutes
              }
            });

            setWeeklyHours((totalMinutes / 60).toFixed(1)); // Convert minutes to hours
          }
        });
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant="primary" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  const doughnutData = {
    labels: ["Pending Requests", "Approved Requests"],
    datasets: [
      {
        data: [pendingRequests, approvedRequests],
        backgroundColor: ["#f8b425", "#28a745"],
        hoverBackgroundColor: ["#ffc107", "#32cd32"],
      },
    ],
  };

  const hoursWorkedData = {
    labels: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    datasets: [
      {
        label: "Hours Worked",
        data: [0, 0, 0, 0, weeklyHours, 0, 0], // Real weekly hours for Friday, rest is zero for this demo
        backgroundColor: "rgba(80, 111, 217, 0.2)",
        borderColor: "#506fd9",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const hoursWorkedOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const navigationItems = [
    { name: "User Profile", icon: "ri-user-line", link: "/pages/profile", requiresPermission: false },
    { name: "Announcements", icon: "ri-notification-line", link: "/announcements", requiresPermission: false },
    { name: "Payrolls", icon: "ri-money-dollar-circle-line", link: "/mypayrolls", permission: "canAccessPayrolls", requiresPermission: true },
    { name: "Clock In", icon: "ri-time-line", link: "/apps/clockinclockout", permission: "canClockInOut", requiresPermission: true },
    { name: "Calendar", icon: "ri-calendar-line", link: "/apps/calendar", permission: "canViewCalendar", requiresPermission: true },
    { name: "Manage Leaves", icon: "ri-leaf-line", link: "/apps/manageleaves", permission: "canManageLeaves", requiresPermission: true },
    { name: "Paycheck Calculator", icon: "ri-calculator-line", link: "/dashboard/paycheckcalculator", requiresPermission: false },
    { name: "Documents", icon: "ri-file-line", link: "/apps/file-manager", permission: "canUploadDocuments", requiresPermission: true },
    { name: "Yearly Document", icon: "ri-booklet-line", link: "/dashboard/yearlydocument", permission: "canViewYearlyDocuments", requiresPermission: true },
    { name: "Invoice", icon: "ri-file-text-line", link: "/dashboard/invoice", permission: "canViewInvoice", requiresPermission: true },
  ];

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* Enhanced Header Section */}
        <div className="text-center mb-5">
          <div className="d-flex align-items-center justify-content-center mb-3">
            <i className="ri-dashboard-3-line fs-1 text-primary me-3"></i>
            <h1 className="display-6 fw-bold text-primary mb-0">Dashboard</h1>
          </div>
          <p className="lead text-muted">Welcome back, {userData?.fullName || "User"}! Here's your overview</p>
        </div>

        {/* Enhanced User Info Cards */}
        <Row className="g-4 mb-5">
          <Col lg={4}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                  style={{ 
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                  }}>
              <div className="bg-gradient-primary text-white p-3" 
                   style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-user-line fs-4 me-3"></i>
                  <h6 className="mb-0 fw-bold">Personal Info</h6>
                </div>
              </div>
              <Card.Body className="text-center p-4">
                <i className="ri-user-line fs-1 text-primary mb-3"></i>
                <h5 className="fw-bold text-dark mb-2">Full Name</h5>
                <p className="text-muted mb-0">{userData?.fullName || "Loading"}</p>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={4}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                  style={{ 
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                  }}>
              <div className="bg-gradient-info text-white p-3" 
                   style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-mail-line fs-4 me-3"></i>
                  <h6 className="mb-0 fw-bold">Contact</h6>
                </div>
              </div>
              <Card.Body className="text-center p-4">
                <i className="ri-mail-line fs-1 text-info mb-3"></i>
                <h5 className="fw-bold text-dark mb-2">Email</h5>
                <p className="text-muted mb-0">{userData?.email || "Loading"}</p>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={4}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                  style={{ 
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                  }}>
              <div className="bg-gradient-warning text-white p-3" 
                   style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-shield-user-line fs-4 me-3"></i>
                  <h6 className="mb-0 fw-bold">Role</h6>
                </div>
              </div>
              <Card.Body className="text-center p-4">
                <i className="ri-shield-user-line fs-1 text-warning mb-3"></i>
                <h5 className="fw-bold text-dark mb-2">User Type</h5>
                <p className="text-muted mb-0">{userData?.role || "Loading"}</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Enhanced Navigation Section */}
        <Card className="border-0 shadow-lg rounded-4 mb-5">
          <div className="bg-gradient-primary text-white p-4 rounded-top-4" 
               style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <div className="d-flex align-items-center justify-content-center">
              <i className="ri-navigation-line fs-4 me-3"></i>
              <h4 className="mb-0 fw-bold">Quick Navigation</h4>
            </div>
          </div>
          <Card.Body className="p-4">
            <Row className="g-4">
              {navigationItems
                .filter((item) => 
                  !item.requiresPermission || permissions.all || permissions[item.permission]
                )
                .map((item, index) => (
                  <Col md={4} lg={3} key={index}>
                    <Card className="border-0 shadow-sm rounded-4 h-100 text-center"
                          style={{ 
                            transition: 'all 0.3s ease',
                            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                          }}>
                      <Card.Body className="p-4">
                        <div className="mb-3">
                          <i className={`${item.icon} fs-1 text-primary`}></i>
                        </div>
                        <Card.Title className="fw-bold text-dark mb-3">{item.name}</Card.Title>
                        <Link to={item.link} className="stretched-link">
                          <Button variant="primary" className="rounded-pill px-4 py-2 fw-semibold">
                            <i className="ri-arrow-right-line me-2"></i>
                            Go
                          </Button>
                        </Link>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
            </Row>
          </Card.Body>
        </Card>

        {/* Enhanced Charts Section */}
        <Row className="g-4">
          <Col lg={6}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="bg-gradient-success text-white p-4" 
                   style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-pie-chart-line fs-4 me-3"></i>
                  <h5 className="mb-0 fw-bold">Leave Request Status</h5>
                </div>
              </div>
              <Card.Body className="p-4">
                <Doughnut data={doughnutData} />
              </Card.Body>
            </Card>
          </Col>
          <Col lg={6}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="bg-gradient-info text-white p-4" 
                   style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-line-chart-line fs-4 me-3"></i>
                  <h5 className="mb-0 fw-bold">Hours Worked This Week</h5>
                </div>
              </div>
              <Card.Body className="p-4">
                <Line data={hoursWorkedData} options={hoursWorkedOptions} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
      <Footer />
    </React.Fragment>
  );
}
